import { useQuery } from '@tanstack/react-query'
import { Camera, ExternalLink, Star, X } from 'lucide-react'
import { useState } from 'react'
import { AButton, Note, Section } from './Section'
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { AuthImage } from '../../components/AuthImage'
import { cn } from '../../lib/cn'
import { get } from '../../lib/api'

/**
 * What the students sent back when they collected their certificates.
 *
 * The reason the claim form exists. Every attendee who finished the trail
 * gave a rating, at least one photograph and often a line about the day —
 * this is where the Committee reads it, and where the photographs are, since
 * they are never given a public URL.
 *
 * Photographs are fetched through the admin API rather than from Nextcloud
 * directly: the app password stays on the server, so a browser is handed
 * bytes and never a link that would work outside this console.
 */

interface ClaimPhoto {
  seq: number
  bytes: number
  mimeType: string
  storage: 'nextcloud' | 'local'
}

interface Claim {
  studentRowId: number
  name: string
  regNo: string | null
  school: string
  certNo: string | null
  rating: number
  comment: string | null
  socialPostUrl: string | null
  submittedAt: string
  photos: ClaimPhoto[]
}

interface Summary {
  claims: number
  completed: number
  averageRating: number | null
  byRating: Record<number, number>
  photos: number
}

/**
 * Rendered through `AuthImage`, never a plain `<img src>`.
 *
 * The route needs an `Authorization` header and an image tag cannot send one.
 * The alternative — a token in the query string — would have made a second,
 * pasteable way to reach a student's photograph, so there is only the one.
 */
const photoUrl = (studentRowId: number, seq: number) =>
  `/api/admin/claims/${studentRowId}/photo/${seq}`

/**
 * The replies counted by school, biggest first.
 *
 * Grouped here rather than on the server because this page already holds
 * every claim — it has to, to show the photographs — so a second round trip
 * would only fetch what is already in memory.
 *
 * Counted against the claims themselves, not against who finished the trail:
 * this answers "which schools replied, and were they happy", and the reply
 * rate per school is a different question that needs the register beside it.
 */
function tallyBySchool(claims: Claim[]) {
  const map = new Map<string, { school: string; count: number; stars: number; photos: number }>()

  for (const c of claims) {
    const row = map.get(c.school) ?? { school: c.school, count: 0, stars: 0, photos: 0 }
    row.count += 1
    row.stars += c.rating
    row.photos += c.photos.length
    map.set(c.school, row)
  }

  return [...map.values()]
    .map((r) => ({ ...r, averageRating: r.stars / r.count }))
    .sort((a, b) => b.count - a.count || a.school.localeCompare(b.school))
}

export function FeedbackSection() {
  const [query, setQuery] = useState('')
  const [stars, setStars] = useState<number | null>(null)
  const [school, setSchool] = useState<string | null>(null)
  const [viewing, setViewing] = useState<{ claim: Claim; seq: number } | null>(null)

  const data = useQuery({
    queryKey: ['admin-claims'],
    queryFn: () => get<{ summary: Summary; claims: Claim[] }>('/api/admin/claims', 'staff'),
    refetchInterval: 30_000,
  })

  const all = data.data?.claims ?? []
  const summary = data.data?.summary

  /*
   * The tally reads the whole set, so the counts do not move as the list is
   * filtered — a breakdown that re-counted itself every time you clicked a
   * school would always show that school at 100%.
   */
  const schools = tallyBySchool(all)
  const busiest = Math.max(...schools.map((s) => s.count), 1)

  const needle = query.trim().toLowerCase()
  const shown = all.filter((c) => {
    if (stars != null && c.rating !== stars) return false
    if (school != null && c.school !== school) return false
    if (!needle) return true
    return (
      c.name.toLowerCase().includes(needle) ||
      (c.regNo ?? '').toLowerCase().includes(needle) ||
      c.school.toLowerCase().includes(needle) ||
      (c.comment ?? '').toLowerCase().includes(needle)
    )
  })

  return (
    <Section
      title="Feedback & photos"
      description="What students sent back when they collected their certificates."
    >
      {data.isLoading && <p className="text-sm text-muted">Loading…</p>}

      {summary && (
        <>
          {/* ── The figures worth knowing at a glance ── */}
          <div className="mb-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <Figure label="Collected" value={String(summary.claims)} />
            <Figure
              label="Finished the trail"
              value={String(summary.completed)}
              sub={
                summary.completed > 0
                  ? `${Math.round((summary.claims / summary.completed) * 100)}% replied`
                  : undefined
              }
            />
            <Figure
              label="Average rating"
              value={summary.averageRating != null ? summary.averageRating.toFixed(2) : '—'}
              sub="out of 5"
            />
            <Figure label="Photos" value={String(summary.photos)} />
          </div>

          {/* ── The spread, which an average alone hides ── */}
          <div className="mb-4 rounded-xl border border-edge bg-white p-3">
            {[5, 4, 3, 2, 1].map((n) => {
              const count = summary.byRating[n] ?? 0
              const pct = summary.claims > 0 ? (count / summary.claims) * 100 : 0
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setStars(stars === n ? null : n)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-edge-soft',
                    stars === n && 'bg-edge-soft',
                  )}
                >
                  <span className="flex w-16 shrink-0 items-center gap-0.5 text-xs font-semibold text-ink-500">
                    {n} <Star className="size-3 fill-amber text-amber" strokeWidth={0} />
                  </span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-edge">
                    <span
                      className="block h-full rounded-full bg-amber transition-[width]"
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="w-10 shrink-0 text-right text-xs tabular-nums text-ink-400">
                    {count}
                  </span>
                </button>
              )
            })}
            {stars != null && (
              <p className="mt-1.5 px-1 text-[11.5px] text-muted">
                Showing {stars}-star replies only —{' '}
                <button type="button" className="underline" onClick={() => setStars(null)}>
                  show all
                </button>
              </p>
            )}
          </div>

          {/* ── Which schools replied, and were they happy ── */}
          {schools.length > 0 && (
            <div className="mb-4 rounded-xl border border-edge bg-white p-3">
              <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-400">
                By school · {schools.length} represented
              </p>

              {/*
                Capped and scrollable. Fourteen schools plus however many
                visiting institutions turn up is not a list that should push
                the replies themselves off the screen.
              */}
              <div className="max-h-64 overflow-y-auto">
                {schools.map((s) => (
                  <button
                    key={s.school}
                    type="button"
                    onClick={() => setSchool(school === s.school ? null : s.school)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-edge-soft',
                      school === s.school && 'bg-edge-soft',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-ink-700">
                      {s.school}
                    </span>

                    <span className="h-2 w-16 shrink-0 overflow-hidden rounded-full bg-edge">
                      <span
                        className="block h-full rounded-full bg-[#1B2A6B]"
                        style={{ width: `${(s.count / busiest) * 100}%` }}
                      />
                    </span>

                    <span className="w-8 shrink-0 text-right text-xs font-bold tabular-nums text-ink-900">
                      {s.count}
                    </span>

                    {/* The average that school gave, which is the point of grouping them. */}
                    <span className="flex w-12 shrink-0 items-center justify-end gap-0.5 text-xs tabular-nums text-ink-400">
                      {s.averageRating.toFixed(1)}
                      <Star className="size-3 fill-amber text-amber" strokeWidth={0} />
                    </span>

                    <span className="hidden w-14 shrink-0 text-right text-xs tabular-nums text-ink-400 sm:block">
                      {s.photos} photo{s.photos === 1 ? '' : 's'}
                    </span>
                  </button>
                ))}
              </div>

              {school != null && (
                <p className="mt-1.5 px-1 text-[11.5px] text-muted">
                  Showing {school} only —{' '}
                  <button type="button" className="underline" onClick={() => setSchool(null)}>
                    show all schools
                  </button>
                </p>
              )}
            </div>
          )}
        </>
      )}

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name, code, school or comment"
        className="mb-3"
      />

      {shown.length === 0 && !data.isLoading && (
        <Note>
          {all.length === 0
            ? 'No certificates have been collected yet. Replies appear here as students finish the trail and fill the form in.'
            : school != null || stars != null
              ? 'No reply matches those filters.'
              : 'Nothing matches that search.'}
        </Note>
      )}

      <div className="grid gap-2.5 lg:grid-cols-2">
        {shown.map((c) => (
          <article key={c.studentRowId} className="rounded-xl border border-edge bg-white p-3">
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-ink-900">{c.name}</p>
                <p className="truncate text-xs text-ink-400">
                  {c.regNo ? `${c.regNo} · ` : ''}
                  {c.school}
                </p>
              </div>
              <span className="flex shrink-0 gap-0.5" aria-label={`${c.rating} out of 5`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={cn(
                      'size-3.5',
                      n <= c.rating ? 'fill-amber text-amber' : 'fill-transparent text-edge',
                    )}
                    strokeWidth={1.8}
                  />
                ))}
              </span>
            </div>

            {c.comment && (
              <p className="mt-2 text-[13px] italic leading-snug text-ink-700">“{c.comment}”</p>
            )}

            {c.photos.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {c.photos.map((p) => (
                  <button
                    key={p.seq}
                    type="button"
                    onClick={() => setViewing({ claim: c, seq: p.seq })}
                    className="size-16 overflow-hidden rounded-lg border border-edge transition-transform hover:scale-105"
                    aria-label={`Photo ${p.seq} from ${c.name}`}
                  >
                    <AuthImage
                      src={photoUrl(c.studentRowId, p.seq)}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-ink-400">
              <span className="inline-flex items-center gap-1">
                <Camera className="size-3" strokeWidth={2.2} />
                {c.photos.length}
              </span>
              {c.certNo && <span className="font-mono tabular-nums">{c.certNo}</span>}
              <span>{new Date(c.submittedAt).toLocaleString()}</span>
              {c.socialPostUrl && (
                <a
                  href={c.socialPostUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 font-semibold text-teal hover:underline"
                >
                  <ExternalLink className="size-3" strokeWidth={2.2} />
                  Their post
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      {/* ── One photo, full size ── */}
      {viewing && (
        <Dialog open onOpenChange={(open) => !open && setViewing(null)}>
          <DialogContent className="max-w-3xl overflow-hidden rounded-2xl p-0">
            <DialogTitle className="sr-only">
              Photo {viewing.seq} from {viewing.claim.name}
            </DialogTitle>

            <AuthImage
              src={photoUrl(viewing.claim.studentRowId, viewing.seq)}
              className="max-h-[75dvh] w-full bg-ink-900 object-contain"
            />

            <div className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-ink-900">{viewing.claim.name}</p>
                <p className="truncate text-xs text-ink-400">
                  Photo {viewing.seq} of {viewing.claim.photos.length}
                </p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                {viewing.claim.photos.length > 1 &&
                  viewing.claim.photos.map((p) => (
                    <button
                      key={p.seq}
                      type="button"
                      onClick={() => setViewing({ claim: viewing.claim, seq: p.seq })}
                      className={cn(
                        'size-7 rounded-md text-xs font-bold tabular-nums',
                        p.seq === viewing.seq
                          ? 'bg-ink-900 text-white'
                          : 'bg-edge-soft text-ink-500 hover:bg-edge',
                      )}
                    >
                      {p.seq}
                    </button>
                  ))}
                <AButton tone="outline" size="sm" onClick={() => setViewing(null)}>
                  <X className="size-3.5" strokeWidth={2.4} />
                  Close
                </AButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Section>
  )
}

function Figure({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-edge bg-white px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <p className="mt-0.5 text-xl font-bold tabular-nums text-ink-900">{value}</p>
      {sub && <p className="text-[11px] text-ink-400">{sub}</p>}
    </div>
  )
}
